import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBuilding,
  faArrowsSpin,
  faPhoneVolume,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons"
import {
  trackingStatusOptions,
  csatStatusOptions,
  statusMeta,
  toneDot,
} from "../statusConfig"

const formatCount = (count) => (Number.isFinite(count) ? count : "—")

const kpiAccents = {
  indigo: { chip: "bg-indigo-500/10 text-indigo-600", bar: "bg-indigo-500" },
  blue: {
    chip: "bg-secondary/10 text-secondary_dark",
    bar: "bg-secondary_dark",
  },
  amber: { chip: "bg-amber-500/10 text-amber-600", bar: "bg-amber-500" },
  emerald: {
    chip: "bg-emerald-500/10 text-emerald-600",
    bar: "bg-emerald-600",
  },
}

const KpiTile = ({
  label,
  count,
  sublabel,
  isLoading,
  isError,
  icon,
  accent,
}) => (
  <div className="relative flex flex-1 min-w-[220px] items-center gap-3.5 overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <span
      className={`absolute inset-y-0 left-0 w-1 ${kpiAccents[accent].bar}`}
    />
    <div
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${kpiAccents[accent].chip}`}
    >
      <FontAwesomeIcon className="h-4.5 w-4.5" icon={icon} />
    </div>
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {label}
      </div>
      <div className="mt-0.5 text-2xl font-bold leading-none tabular-nums text-gray-900">
        {isLoading ? (
          <span className="inline-block h-6 w-12 animate-pulse rounded bg-gray-100 align-middle" />
        ) : isError ? (
          "—"
        ) : (
          formatCount(count)
        )}
      </div>
      {sublabel && !isLoading && !isError && (
        <div className="mt-1 truncate text-xs text-gray-400">{sublabel}</div>
      )}
    </div>
  </div>
)

// One row of the pipeline bar — index, label + tone dot, a track/fill bar
// sized relative to the largest count across ALL 9 stages (so bar lengths
// are comparable pipeline-wide, not just within their own group), and the
// raw count on the right.
const StageBar = ({
  index,
  label,
  count,
  isLoading,
  isError,
  tone,
  maxCount,
  milestone,
}) => {
  const widthPct =
    isLoading || isError || !Number.isFinite(count) || !maxCount
      ? 0
      : Math.max((count / maxCount) * 100, count > 0 ? 3 : 0)

  return (
    <div className="grid grid-cols-[18px_minmax(140px,200px)_1fr_32px] items-center gap-2 py-1">
      <div className="text-center text-xs font-semibold tabular-nums text-gray-400">
        {index}
      </div>
      <div
        className={`flex items-center gap-1.5 truncate text-[13px] ${milestone ? "font-semibold text-gray-900" : "text-gray-600"}`}
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${toneDot[tone]}`} />
        <span className="truncate">{label}</span>
      </div>
      <div className="h-5 overflow-hidden rounded-md bg-gray-100">
        {!isLoading && !isError && (
          <div
            className={`h-full rounded-md transition-all duration-700 ${toneDot[tone]}`}
            style={{ width: `${widthPct}%` }}
          />
        )}
      </div>
      <div className="text-right text-sm font-bold tabular-nums text-gray-900">
        {isLoading ? (
          <span className="inline-block h-4 w-6 animate-pulse rounded bg-gray-100" />
        ) : isError ? (
          "—"
        ) : (
          formatCount(count)
        )}
      </div>
    </div>
  )
}

const GroupTag = ({ children, tone }) => (
  <span
    className={`rounded px-2 py-0.5 text-[11px] font-semibold text-white ${toneDot[tone]}`}
  >
    {children}
  </span>
)

const OverviewStats = ({ grandTotal, trackingCounts, csatCounts, centers }) => {
  const ceoDue = csatCounts.CEO_OUTREACH_PENDING
  const allCounts = [
    ...trackingStatusOptions.map(({ value }) => trackingCounts[value]),
    ...csatStatusOptions.map(({ value }) => csatCounts[value]),
  ]
  const maxCount = Math.max(
    1,
    ...allCounts.filter((c) => Number.isFinite(c?.count)).map((c) => c.count)
  )

  return (
    <div className="mb-5 flex flex-col gap-4">
      <div className="flex flex-wrap gap-3">
        <KpiTile
          accent="indigo"
          count={centers.total}
          icon={faBuilding}
          isError={centers.isError}
          isLoading={centers.isLoading}
          label="Tracked"
          sublabel={
            Number.isFinite(grandTotal.count)
              ? `${grandTotal.count} studies in the pipeline`
              : undefined
          }
        />
        <KpiTile
          accent="blue"
          count={centers.inProgress}
          icon={faArrowsSpin}
          isError={centers.isError}
          isLoading={centers.isLoading}
          label="In Progress"
          sublabel="still moving through stages"
        />
        <KpiTile
          accent="amber"
          count={ceoDue?.count}
          icon={faPhoneVolume}
          isError={ceoDue?.isError}
          isLoading={ceoDue?.isLoading}
          label="Awaiting CEO Call"
          sublabel={
            Number.isFinite(centers.ceoOutreachDueCenters)
              ? `across ${centers.ceoOutreachDueCenters} center${centers.ceoOutreachDueCenters === 1 ? "" : "s"}`
              : undefined
          }
        />
        <KpiTile
          accent="emerald"
          count={centers.fullyConfirmed}
          icon={faCircleCheck}
          isError={centers.isError}
          isLoading={centers.isLoading}
          label="Completed"
          sublabel="all tracked cases closed out"
        />
      </div>

      {/*Todo: Hiding this overview stats for now as discussed */}

      {/* <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-gray-900">
            Where every tracked case sits
          </h2>
          <p className="mt-0.5 text-xs text-gray-400">
            One pipeline, two deals. The case deal runs stages 1–4; when the
            report is signed, a report-confirmation deal is created at stage 5
            and runs to 9.
          </p>
        </div>

        <div className="flex flex-col gap-6 md:flex-row md:items-start">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <GroupTag tone="blue">Case deal</GroupTag>
              <span className="text-xs font-semibold text-gray-900">
                Stages 1–4
              </span>
            </div>
            <div>
              {trackingStatusOptions.map(({ value, label }, i) => (
                <StageBar
                  key={value}
                  count={trackingCounts[value]?.count}
                  index={i + 1}
                  isError={trackingCounts[value]?.isError}
                  isLoading={trackingCounts[value]?.isLoading}
                  label={label}
                  maxCount={maxCount}
                  milestone={value === "REPORT_SIGNED"}
                  tone={statusMeta[value].tone}
                />
              ))}
            </div>
          </div>

          <div className="hidden w-px self-stretch bg-gray-200 md:block" />

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <GroupTag tone="emerald">Report confirmation deal</GroupTag>
              <span className="text-xs font-semibold text-gray-900">
                Stages 5–9
              </span>
            </div>
            <div>
              {csatStatusOptions.map(({ value, label }, i) => (
                <StageBar
                  key={value}
                  count={csatCounts[value]?.count}
                  index={i + 5}
                  isError={csatCounts[value]?.isError}
                  isLoading={csatCounts[value]?.isLoading}
                  label={label}
                  maxCount={maxCount}
                  tone={statusMeta[value].tone}
                />
              ))}
            </div>
          </div>
        </div>
      </div> */}
    </div>
  )
}

export default OverviewStats
